import React, { useEffect, useState } from 'react';
import {
  View,
  Image,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  useWindowDimensions,
} from 'react-native';
import { useIsFocused, useRoute } from '@react-navigation/native';
import { AntDesign, MaterialCommunityIcons, Ionicons, Feather } from '@expo/vector-icons';
import Text from '../../components/text';
import Urls from '../../services/Urls';
import ajax from '../../helpers/ajaxHelper';
import drawerHoc from '../../components/drawerHOC/DrawerHOC';
import getPlatformType from '../../helpers/helpers';
import { navigate } from '../../navigation/RootNavigation';
import PopUp from '../../components/popUp/PopupView';
import { usePropertyRouteParams } from './sharedComponents';

/* ─── status helper ─────────────────────────────────── */
const getStatus = (status) => {
  if (status === 2) return { label: 'Complete', color: '#16a34a', bg: '#dcfce7', dot: '#16a34a' };
  if (status === 1) return { label: 'In Progress', color: '#d97706', bg: '#fef3c7', dot: '#d97706' };
  return { label: 'Not Started', color: '#6b7280', bg: '#f3f4f6', dot: '#9ca3af' };
};

/* ─── price formatter helper ──────────────────────────── */
const formatPriceDisplay = (rentAmount, propertyWorth, priceWorth) => {
  if (rentAmount) {
    const num = Number(rentAmount);
    const formatted = isNaN(num) ? rentAmount : num.toLocaleString();
    return `£${formatted}/mo`;
  }
  const worth = propertyWorth || priceWorth;
  if (worth) {
    const num = Number(worth);
    const formatted = isNaN(num) ? worth : num.toLocaleString();
    return `£${formatted}`;
  }
  return '—';
};

/* ─── section items config ───────────────────────────── */
const getSections = (state, navigation) => [
  {
    key: 'location',
    icon: 'map-marker',
    title: 'Location & Address',
    description: 'Add precise location and address details for the property.',
    status: state.location_details_status,
    onPress: () => navigation.navigate('PropertyAddress', {
      status: state.location_details_status,
      id: state.id, name: state.property_name, key: 'location_details_status',
    }),
  },
  {
    key: 'landlord',
    icon: 'account-outline',
    title: 'Landlord Details',
    description: 'Information regarding the legal owner or managing agent.',
    status: state.landlord_details_status,
    onPress: () => navigation.navigate('LandlordDetails', {
      status: state.landlord_details_status,
      id: state.id, name: state.property_name, key: 'landlord_details_status',
    }),
  },
  {
    key: 'property',
    icon: 'home-outline',
    title: 'Property Details',
    description: 'Square footage, rooms, amenities, and floor plans.',
    status: state.room_details_status,
    onPress: () => navigation.navigate('PropertyDetails', {
      status: state.room_details_status,
      id: state.id, name: state.property_name, key: 'room_details_status',
    }),
  },
  {
    key: 'certification',
    icon: 'certificate',
    title: 'Certification',
    description: 'Compliance certificates, EPC, Gas Safety, and Electric.',
    status: state.certificate_details_status,
    onPress: () => navigation.navigate('Certification', {
      status: state.certificate_details_status,
      id: state.id, name: state.property_name, key: 'certificate_details_status',
    }),
  },
  {
    key: 'requirements',
    icon: 'clipboard-check-outline',
    title: 'Requirements',
    description: 'Mandatory safety checklists and habitation standards.',
    status: state.property_requirements_status,
    onPress: () => navigation.navigate('Requirements', {
      status: state.property_requirements_status,
      id: state.id, name: state.property_name, key: 'property_requirements_status',
    }),
  },
  {
    key: 'licensing',
    icon: 'shield-check-outline',
    title: 'Licensing',
    description: 'Local authority licences and selective licensing info.',
    status: state.license_details_status,
    onPress: () => navigation.navigate('Licensing', {
      status: state.license_details_status,
      id: state.id, name: state.property_name, key: 'license_details_status',
    }),
  },
  {
    key: 'finance',
    icon: 'bank-outline',
    title: 'Finance',
    description: 'Mortgage details, insurance, and tax information.',
    status: state.finance_details_status,
    onPress: () => navigation.navigate('financeDetails', {
      status: state.finance_details_status,
      id: state.id, name: state.property_name, key: 'finance_details_status',
    }),
  },
  {
    key: 'utility',
    icon: 'lightning-bolt-outline',
    title: 'Utility',
    description: 'Gas, Electricity, Water suppliers and account details.',
    status: state.utilities_details_status,
    onPress: () => navigation.navigate('Utilities', {
      status: state.utilities_details_status,
      id: state.id, name: state.property_name, key: 'utilities_details_status',
    }),
  },
];

/* ─── Status Badge ───────────────────────────────────── */
const StatusBadge = ({ status }) => {
  const s = getStatus(status);
  return (
    <View style={[st.badge, { backgroundColor: s.bg }]}>
      <Text style={[st.badgeText, { color: s.color }]}>{s.label}</Text>
    </View>
  );
};

/* ─── Section Card (web 2-col grid) ─────────────────── */
const SectionCard = ({ item }) => {
  const s = getStatus(item.status);
  return (
    <TouchableOpacity style={st.sectionCard} onPress={item.onPress} activeOpacity={0.75}>
      <View style={st.sectionCardInner}>
        <View style={st.sectionCardTop}>
          <View style={[st.sectionDot, { backgroundColor: s.dot + '33' }]}>
            <MaterialCommunityIcons name={item.icon} size={16} color={s.dot} />
          </View>
          <Text style={st.sectionTitle} numberOfLines={1}>{item.title}</Text>
          <StatusBadge status={item.status} />
          <AntDesign name="right" size={13} color="#ccc" />
        </View>
        <Text style={st.sectionDesc} numberOfLines={2}>{item.description}</Text>
      </View>
    </TouchableOpacity>
  );
};

/* ─── Section Row (mobile list) ─────────────────────── */
const SectionRow = ({ item }) => {
  const s = getStatus(item.status);
  return (
    <TouchableOpacity style={st.sectionRow} onPress={item.onPress} activeOpacity={0.75}>
      <View style={[st.sectionRowDot, { backgroundColor: s.dot + '22' }]}>
        <MaterialCommunityIcons name={item.icon} size={18} color={s.dot} />
      </View>
      <View style={{ flex: 1 }}>
        <Text style={st.sectionRowTitle}>{item.title}</Text>
        <Text style={st.sectionRowDesc} numberOfLines={2}>{item.description}</Text>
      </View>
      <View style={{ alignItems: 'flex-end', gap: 6 }}>
        <StatusBadge status={item.status} />
        <AntDesign name="right" size={13} color="#ccc" />
      </View>
    </TouchableOpacity>
  );
};

/* ─── Main Component ─────────────────────────────────── */
const PropertySection = ({ navigation }) => {
  const routeParams = usePropertyRouteParams();
  const route = useRoute();
  const focused = useIsFocused();
  const targetId = routeParams.id;

  const [state, setState] = useState({
    postcode: '', houseName: '', address1: '', city: '',
    country: '', property_name: '', property_worth: '', price_worth: '',
    mortgage_provider: '', id: '', hubita_id: '',
    percentage: 0,
    location_details_status: 0, room_details_status: 0,
    certificate_details_status: 0, property_requirements_status: 0,
    license_details_status: 0, finance_details_status: 0,
    utilities_details_status: 0, landlord_details_status: 0,
    tenant_details_status: 0,
    rent_amount: '', deposit_amount: '', property_type: '',
    epc_rating: '', council_tax_band: '', images: [],
  });

  const [showArchiveModal, setShowArchiveModal] = useState(false);

  useEffect(() => {
    if (focused && targetId) _getDetails(targetId);
  }, [focused, targetId]);

  const _onArchiveProperty = () => {
    const targetId = state?.id || route?.params?.id;
    if (targetId) {
      ajax
        .delete(`${Urls.Properties}/${targetId}`)
        .then(({ data }) => {
          setShowArchiveModal(false);
          if (data?.status) {
            navigation.navigate('Properties');
          }
        })
        .catch((err) => {
          setShowArchiveModal(false);
          console.log('error archiving property', err);
        });
    }
  };

  const _getDetails = (id) => {
    ajax.get(Urls.Properties + '/' + id)
      .then(({ data }) => {
        if (data.status) {
          const payload = data?.payload || {};
          setState((prev) => ({
            ...prev,
            address1: payload?.address_line1 || '',
            postcode: payload?.postcode || '',
            property_name: payload?.name || '',
            city: payload?.city || '',
            houseName: payload?.house_name_num || '',
            property_worth: payload?.price_worth || payload?.property_worth || payload?.price || '',
            price_worth: payload?.price_worth || payload?.property_worth || payload?.price || '',
            mortgage_provider: payload?.mortgage_provider || '',
            hubita_id: payload?.hubita_id || '',
            id: payload?.id || '',
            country: payload?.country || '',
            percentage: payload?.percentage || 0,
            rent_amount: payload?.rent_amount || payload?.amount || payload?.rentAmount || '',
            deposit_amount: payload?.deposit_amount || payload?.depositAmount || '',
            property_type: payload?.property_type || '',
            epc_rating: payload?.epc_rating || '',
            council_tax_band: payload?.council_tax_band || '',
            images: payload?.images || [],
            location_details_status: payload?.location_details_status,
            room_details_status: payload?.room_details_status,
            certificate_details_status: payload?.certificate_details_status,
            property_requirements_status: payload?.property_requirements_status,
            license_details_status: payload?.license_details_status,
            finance_details_status: payload?.finance_details_status,
            utilities_details_status: payload?.utilities_details_status,
            landlord_details_status: payload?.landlord_details_status,
            tenant_details_status: payload?.tenant_details_status,
          }));
        }
      })
      .catch((err) => console.log('error fetching property details', err));
  };

  const sections = getSections(state, navigation);
  const pct = Math.min(100, Math.round(state.percentage || 0));
  const propertyImage = state.images?.[0]?.openbrixName || null;
  const fullAddress = [state.houseName, state.address1, state.city, state.postcode]
    .filter(Boolean).join(', ');

  const displayPrice = formatPriceDisplay(state.rent_amount, state.property_worth, state.price_worth);

  const { width } = useWindowDimensions();
  const isDesktop = width > 1024;

  /* ── MOBILE & TABLET (Row alignment layout) ──────────── */
  if (!isDesktop) {
    return (
      <View style={st.mobileRoot}>
        <ScrollView
          style={{ flex: 1 }}
          contentContainerStyle={st.mobileScroll}
          showsVerticalScrollIndicator={false}
        >
          {/* Property hero card */}
          <View style={st.mobilePropCard}>
            {propertyImage ? (
              <Image source={{ uri: propertyImage }} style={st.mobilePropImage} />
            ) : (
              <View style={st.mobilePropImagePlaceholder}>
                <Ionicons name="home-outline" size={40} color="#d0b8f0" />
              </View>
            )}
            <View style={st.mobilePropBody}>
              <View style={{ flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', width: '100%' }}>
                <View style={{ flex: 1, marginRight: 8 }}>
                  <Text style={st.mobilePropName}>{state.property_name}</Text>
                  <Text style={st.mobilePropAddr} numberOfLines={2}>{fullAddress}</Text>
                </View>
                <TouchableOpacity
                  style={st.mobileArchiveBtn}
                  onPress={() => setShowArchiveModal(true)}
                  activeOpacity={0.8}
                >
                  <Feather name="archive" size={13} color="#EF4444" style={{ marginRight: 4 }} />
                  <Text style={st.mobileArchiveBtnText}>Archive</Text>
                </TouchableOpacity>
              </View>

              {/* Stats grid */}
              <View style={st.statsGrid}>
                <View style={st.statItem}>
                  <Text style={st.statLabel}>PRICE</Text>
                  <Text style={st.statValue}>{displayPrice}</Text>
                </View>
              </View>
            </View>
          </View>

          {/* Setup Progress card */}
          <View style={st.progressCard}>
            <View style={st.progressCardTop}>
              <Text style={st.progressCardTitle}>Setup Progress</Text>
              <Text style={st.progressPct}>{pct}% Complete</Text>
            </View>
            <View style={st.progressTrack}>
              <View style={[st.progressFill, { width: `${pct}%` }]} />
            </View>
            <TouchableOpacity
              style={{ marginTop: 12 }}
              onPress={() => navigation.navigate('PropertyInfo', { id: route?.params?.id })}
            >
              <Text style={st.heroMoreLink}>More Details ›</Text>
            </TouchableOpacity>
          </View>

          {/* Setup Checklist */}
          <View style={{ marginBottom: 12 }}>
            <Text style={st.checklistHeader}>Setup Checklist</Text>
            {sections.map((item) => (
              <SectionRow key={item.key} item={item} />
            ))}
          </View>

          {/* Save button */}
          <TouchableOpacity
            style={st.mobileSaveBtn}
            onPress={() => navigate('PropertyInfo', { id: route?.params?.id })}
          >
            <Text style={st.mobileSaveBtnText}>Save Progress</Text>
          </TouchableOpacity>
        </ScrollView>

        <PopUp
          modalVisible={showArchiveModal}
          titleText={'Archive Property?'}
          titleColor={'#6C2BD9'}
          customSubtitle={
            <Text style={{ textAlign: 'center', color: '#4B5563', fontSize: 14, marginTop: 6, lineHeight: 20 }}>
              Are you sure you want to archive this property? It can be restored anytime from the Archive Manager.
            </Text>
          }
          actionButtons={[
            {
              text: 'Archive',
              width: 100,
              color: '#EF4444',
              onPress: _onArchiveProperty,
            },
            {
              text: 'Cancel',
              width: 100,
              onPress: () => setShowArchiveModal(false),
            },
          ]}
          closePopup={() => setShowArchiveModal(false)}
        />
      </View>
    );
  }

  /* ── WEB / TABLET ────────────────────────────────────── */
  return (
    <View style={st.root}>
      {/* Property hero card */}
      <View style={st.heroBg}>
        <View style={st.heroCard}>
          {/* Left: image */}
          <View style={st.heroImageWrap}>
            {propertyImage ? (
              <Image source={{ uri: propertyImage }} style={st.heroImage} />
            ) : (
              <View style={[st.heroImage, st.heroImagePlaceholder]}>
                <Ionicons name="home-outline" size={48} color="#d0b8f0" />
              </View>
            )}
          </View>

          {/* Right: info */}
          <View style={st.heroInfo}>
            <View style={st.heroTopRow}>
              <View>
                <Text style={st.heroPropName}>{state.property_name}</Text>
                <Text style={st.heroAddr}>{fullAddress}</Text>
              </View>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                <TouchableOpacity
                  style={st.webArchiveBtn}
                  onPress={() => setShowArchiveModal(true)}
                  activeOpacity={0.85}
                >
                  <Feather name="archive" size={14} color="#EF4444" style={{ marginRight: 6 }} />
                  <Text style={st.webArchiveBtnText}>Archive Property</Text>
                </TouchableOpacity>
                {state.location_details_status !== undefined && (
                  <View style={st.actionRequired}>
                    <AntDesign name="exclamationcircle" size={14} color="#d97706" />
                    <Text style={st.actionRequiredText}>Action Required</Text>
                  </View>
                )}
              </View>
            </View>

            {/* Property stats row */}
            <View style={st.heroStats}>
              <View style={st.heroStat}>
                <Text style={st.heroStatLabel}>Price</Text>
                <Text style={st.heroStatValue}>{displayPrice}</Text>
              </View>
            </View>

            {/* Progress bar */}
            <View style={st.heroProgress}>
              <View style={st.heroProgressTrack}>
                <View style={[st.heroProgressFill, { width: `${pct}%` }]} />
              </View>
              <Text style={st.heroProgressLabel}>{pct}% Setup Complete</Text>
              <TouchableOpacity
                onPress={() => navigation.navigate('PropertyInfo', { id: route?.params?.id })}
              >
                <Text style={st.heroMoreLink}>More Details ›</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </View>

      {/* Setup Checklist — 2-column grid */}
      <View style={st.checklistSection}>
        <Text style={st.checklistHeading}>Setup Checklist</Text>
        <View style={st.grid}>
          {sections.map((item) => (
            <SectionCard key={item.key} item={item} />
          ))}
        </View>

        {/* Save Progress button */}
        <TouchableOpacity
          style={st.saveBtn}
          onPress={() => navigate('PropertyInfo', { id: route?.params?.id })}
        >
          <Text style={st.saveBtnText}>Save Progress</Text>
        </TouchableOpacity>
      </View>

      <PopUp
        modalVisible={showArchiveModal}
        titleText={'Archive Property?'}
        titleColor={'#6C2BD9'}
        customSubtitle={
          <Text style={{ textAlign: 'center', color: '#4B5563', fontSize: 14, marginTop: 6, lineHeight: 20 }}>
            Are you sure you want to archive this property? It can be restored anytime from the Archive Manager.
          </Text>
        }
        actionButtons={[
          {
            text: 'Archive',
            width: 100,
            color: '#EF4444',
            onPress: _onArchiveProperty,
          },
          {
            text: 'Cancel',
            width: 100,
            onPress: () => setShowArchiveModal(false),
          },
        ]}
        closePopup={() => setShowArchiveModal(false)}
      />
    </View>
  );
};

/* ─── Styles ─────────────────────────────────────────── */
const st = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: '#F5F7FB',
  },
  heroBg: {
    backgroundColor: '#FFFFFF',
    paddingHorizontal: 32,
    paddingVertical: 24,
    borderBottomWidth: 1,
    borderBottomColor: '#F3F4F6',
  },
  heroCard: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 24,
    maxWidth: 1200,
  },
  heroImageWrap: {
    width: 220,
    height: 140,
    borderRadius: 14,
    overflow: 'hidden',
    backgroundColor: '#F0E8FF',
  },
  heroImage: {
    width: '100%',
    height: '100%',
    resizeMode: 'cover',
  },
  heroImagePlaceholder: {
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#F0E8FF',
  },
  heroInfo: {
    flex: 1,
  },
  heroTopRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  heroPropName: {
    fontSize: 22,
    fontWeight: '700',
    color: '#111827',
    marginBottom: 4,
  },
  heroAddr: {
    fontSize: 13,
    color: '#6B7280',
  },
  webArchiveBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FEE2E2',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 20,
  },
  webArchiveBtnText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#EF4444',
  },
  actionRequired: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: '#FEF3C7',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 20,
  },
  actionRequiredText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#D97706',
  },
  heroStats: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 24,
    marginBottom: 16,
  },
  heroStat: {
    flexDirection: 'column',
  },
  heroStatLabel: {
    fontSize: 11,
    fontWeight: '700',
    color: '#9CA3AF',
    textTransform: 'uppercase',
  },
  heroStatValue: {
    fontSize: 18,
    fontWeight: '700',
    color: '#6C2BD9',
    marginTop: 2,
  },
  heroProgress: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  heroProgressTrack: {
    flex: 1,
    height: 8,
    backgroundColor: '#E5E7EB',
    borderRadius: 4,
    overflow: 'hidden',
  },
  heroProgressFill: {
    height: '100%',
    backgroundColor: '#6C2BD9',
    borderRadius: 4,
  },
  heroProgressLabel: {
    fontSize: 12,
    fontWeight: '600',
    color: '#4B5563',
  },
  heroMoreLink: {
    fontSize: 12,
    fontWeight: '700',
    color: '#6C2BD9',
  },
  checklistSection: {
    paddingHorizontal: 32,
    paddingVertical: 24,
    maxWidth: 1200,
  },
  checklistHeading: {
    fontSize: 18,
    fontWeight: '700',
    color: '#111827',
    marginBottom: 16,
  },
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
    marginBottom: 24,
  },
  sectionCard: {
    width: '48.8%',
    backgroundColor: '#FFFFFF',
    borderRadius: 14,
    padding: 16,
    borderWidth: 1,
    borderColor: '#F3F4F6',
  },
  sectionCardInner: {
    flexDirection: 'column',
  },
  sectionCardTop: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginBottom: 8,
  },
  sectionDot: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  sectionTitle: {
    fontSize: 15,
    fontWeight: '700',
    color: '#111827',
    flex: 1,
  },
  sectionDesc: {
    fontSize: 13,
    color: '#6B7280',
    lineHeight: 18,
  },
  badge: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 12,
  },
  badgeText: {
    fontSize: 11,
    fontWeight: '700',
  },
  saveBtn: {
    backgroundColor: '#6C2BD9',
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 24,
    alignSelf: 'flex-start',
  },
  saveBtnText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '700',
  },

  /* Mobile styles */
  mobileRoot: {
    flex: 1,
    backgroundColor: '#F5F7FB',
  },
  mobileScroll: {
    padding: 14,
  },
  mobilePropCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 14,
    overflow: 'hidden',
    marginBottom: 14,
    borderWidth: 1,
    borderColor: '#F3F4F6',
  },
  mobilePropImage: {
    width: '100%',
    height: 160,
    resizeMode: 'cover',
  },
  mobilePropImagePlaceholder: {
    width: '100%',
    height: 140,
    backgroundColor: '#F0E8FF',
    alignItems: 'center',
    justifyContent: 'center',
  },
  mobilePropBody: {
    padding: 14,
  },
  mobilePropName: {
    fontSize: 18,
    fontWeight: '700',
    color: '#111827',
    marginBottom: 2,
  },
  mobilePropAddr: {
    fontSize: 12,
    color: '#6B7280',
  },
  mobileArchiveBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FEE2E2',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 14,
  },
  mobileArchiveBtnText: {
    fontSize: 11,
    fontWeight: '600',
    color: '#EF4444',
  },
  statsGrid: {
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#F3F4F6',
  },
  statItem: {
    flexDirection: 'column',
  },
  statLabel: {
    fontSize: 10,
    fontWeight: '700',
    color: '#9CA3AF',
  },
  statValue: {
    fontSize: 16,
    fontWeight: '700',
    color: '#6C2BD9',
    marginTop: 2,
  },
  progressCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 14,
    padding: 14,
    marginBottom: 14,
    borderWidth: 1,
    borderColor: '#F3F4F6',
  },
  progressCardTop: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  progressCardTitle: {
    fontSize: 14,
    fontWeight: '700',
    color: '#111827',
  },
  progressPct: {
    fontSize: 12,
    fontWeight: '700',
    color: '#6C2BD9',
  },
  progressTrack: {
    height: 6,
    backgroundColor: '#E5E7EB',
    borderRadius: 3,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    backgroundColor: '#6C2BD9',
    borderRadius: 3,
  },
  checklistHeader: {
    fontSize: 16,
    fontWeight: '700',
    color: '#111827',
    marginBottom: 10,
  },
  sectionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 12,
    marginBottom: 8,
    gap: 10,
    borderWidth: 1,
    borderColor: '#F3F4F6',
  },
  sectionRowDot: {
    width: 34,
    height: 34,
    borderRadius: 17,
    alignItems: 'center',
    justifyContent: 'center',
  },
  sectionRowTitle: {
    fontSize: 14,
    fontWeight: '700',
    color: '#111827',
  },
  sectionRowDesc: {
    fontSize: 12,
    color: '#6B7280',
    marginTop: 2,
  },
  mobileSaveBtn: {
    backgroundColor: '#6C2BD9',
    paddingVertical: 13,
    borderRadius: 24,
    alignItems: 'center',
    marginTop: 8,
    marginBottom: 20,
  },
  mobileSaveBtnText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '700',
  },
});

export default drawerHoc({
  Component: PropertySection,
  showBackButton: true,
});
